import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MobileBottomSheet, SheetOptionButton } from '@/components/mobile/MobileBottomSheet'
import { vocabularyApi } from '@/api/vocabulary'
import { useSettingsStore } from '@/store/settingsStore'
import type { UserWordSummary } from '@/types/user'

interface AddToCollectionSheetProps {
  word: UserWordSummary | null
  open: boolean
  onClose: () => void
}

export function AddToCollectionSheet({ word, open, onClose }: AddToCollectionSheetProps) {
  const queryClient = useQueryClient()
  const nativeLanguage = useSettingsStore((s) => s.settings?.nativeLanguage ?? 'en')

  const { data: collections = [] } = useQuery({
    queryKey: ['collections', nativeLanguage],
    queryFn: () => vocabularyApi.getCollections().then((r) => r.data),
    enabled: open,
  })

  const addMutation = useMutation({
    mutationFn: ({ collectionId, userWordId }: { collectionId: string; userWordId: string }) =>
      vocabularyApi.addWordToCollection(collectionId, userWordId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collections'] })
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
      onClose()
    },
  })

  if (!word) return null

  return (
    <MobileBottomSheet
      open={open}
      title={`Add "${word.word}" to collection`}
      onClose={onClose}
      testId="add-to-collection-sheet"
    >
      {collections.length === 0 ? (
        <p className="px-1 py-2 text-sm text-muted-foreground">
          No collections yet. Open Collections to create one first.
        </p>
      ) : (
        <div className="space-y-1">
          {collections.map((col) => (
            <SheetOptionButton
              key={col.id}
              active={false}
              label={col.name}
              count={col.wordCount}
              onClick={() => addMutation.mutate({ collectionId: col.id, userWordId: word.id })}
            />
          ))}
        </div>
      )}
    </MobileBottomSheet>
  )
}
